import { SafeEvent } from "@playarena/shared";
import { Calendar, MapPin, Users, Clock, Star } from "lucide-react";
import type React from "react";
import { Link } from "react-router-dom";

import { formatDateTime, formatCurrency, formatEventDuration } from "../../../utils/helpers";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader } from "../../ui/card";

interface EventCardProps {
  event: SafeEvent;
  viewMode?: "grid" | "list";
}

const EventCard: React.FC<EventCardProps> = ({ event, viewMode = "grid" }) => {
  if (viewMode === "list") {
    return (
      <Card className='overflow-hidden transition-shadow hover:shadow-lg'>
        <div className='flex'>
          <div className='h-32 w-48 flex-shrink-0 bg-gradient-to-r from-blue-500 to-purple-500' />
          <div className='flex-1 p-6'>
            <div className='mb-2 flex items-start justify-between'>
              <div>
                <div className='mb-2 flex items-center gap-2'>
                  <Badge
                    variant={event.status === "active" ? "default" : "secondary"}
                    className='capitalize'
                  >
                    {event.status}
                  </Badge>
                  <Badge variant='outline' className='capitalize'>
                    {event.ground?.type}
                  </Badge>
                </div>
                <h3 className='mb-1 text-xl font-semibold'>{event.title}</h3>
                <p className='line-clamp-2 text-muted-foreground'>{event.description}</p>
              </div>
              <div className='text-right'>
                <div className='text-lg font-bold'>
                  From {formatCurrency(Math.min(...event.ticketTypes.map(t => t.price)))}
                </div>
                <div className='text-sm text-muted-foreground'>per ticket</div>
              </div>
            </div>

            <div className='mb-3 flex items-center gap-4 text-sm text-muted-foreground'>
              <div className='flex items-center gap-1'>
                <Calendar className='h-4 w-4' />
                <span>{formatDateTime(event.datetime)}</span>
              </div>
              <div className='flex items-center gap-1'>
                <Clock className='h-4 w-4' />
                <span>{formatEventDuration(event.duration)}</span>
              </div>
              <div className='flex items-center gap-1'>
                <MapPin className='h-4 w-4' />
                <span>{event.ground?.location.city}</span>
              </div>
            </div>

            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                <div className='flex items-center text-sm text-muted-foreground'>
                  <Users className='mr-1 h-4 w-4' />
                  {event.ground?.capacity.toLocaleString()}
                </div>
                <div className='flex items-center text-sm text-muted-foreground'>
                  <Star className='mr-1 h-4 w-4 fill-yellow-400 text-yellow-400' />
                  4.5 (89 reviews)
                </div>
              </div>
              <Button asChild>
                <Link to={`/events/${event.id}`}>View Details</Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className='overflow-hidden transition-shadow hover:shadow-lg'>
      <div className='relative aspect-video bg-gradient-to-r from-blue-500 to-purple-500'>
        <div className='absolute left-3 top-3'>
          <Badge
            variant={event.status === "active" ? "default" : "secondary"}
            className='capitalize'
          >
            {event.status}
          </Badge>
        </div>
        <div className='absolute right-3 top-3'>
          <Badge variant='outline' className='bg-white/90 capitalize'>
            {event.ground?.type}
          </Badge>
        </div>
      </div>
      <CardHeader className='pb-3'>
        <div className='mb-2 flex items-center justify-between'>
          <div className='flex items-center text-sm text-muted-foreground'>
            <Calendar className='mr-1 h-4 w-4' />
            <span>{formatDateTime(event.datetime)}</span>
          </div>
          <div className='flex items-center text-sm text-muted-foreground'>
            <Star className='mr-1 h-4 w-4 fill-yellow-400 text-yellow-400' />
            4.5
          </div>
        </div>
        <h3 className='line-clamp-2 text-lg font-semibold'>{event.title}</h3>
        <p className='line-clamp-2 text-sm text-muted-foreground'>{event.description}</p>
      </CardHeader>
      <CardContent>
        <div className='mb-3 flex items-center justify-between'>
          <div className='flex items-center text-sm text-muted-foreground'>
            <MapPin className='mr-1 h-4 w-4' />
            <span>{event.ground?.location.city}</span>
          </div>
          <div className='flex items-center text-sm text-muted-foreground'>
            <Clock className='mr-1 h-4 w-4' />
            <span>{formatEventDuration(event.duration)}</span>
          </div>
        </div>

        <div className='mb-4 flex items-center justify-between'>
          <div className='text-lg font-bold'>
            From {formatCurrency(Math.min(...event.ticketTypes.map(t => t.price)))}
          </div>
          <div className='text-sm text-muted-foreground'>
            {event.ticketTypes.reduce((total, t) => total + t.available, 0)} tickets left
          </div>
        </div>

        <Button asChild className='w-full'>
          <Link to={`/events/${event.id}`}>Book Now</Link>
        </Button>
      </CardContent>
    </Card>
  );
};

export default EventCard;
